import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { fetchManagerPerformanceDashboardRequest, fetchPerformanceCampaignCardsRequest, fetchPerformanceStepperRequest, fetchProgressConsolidationRequest, finalizeManagerPerformanceProgressRequest, updatePerformanceCurrentStepRequest } from "../../../redux/actions/performanceActions";
import PerformanceStepper, { PerformanceStepArrows } from "../Collaborateur/PerformanceStepper";
import { asArray, sortCampaignsByDefault, statusTone, stepLabels, formatStatus } from "../Collaborateur/performanceUi";
import "../PerformanceStyles.css";
import ObjectifsNMoins1ManagerStep from "./steps/ObjectifsNMoins1ManagerStep";
import CompetencesGeneriquesManagerStep from "./steps/CompetencesGeneriquesManagerStep";
import CompetencesSpecifiquesManagerStep from "./steps/CompetencesSpecifiquesManagerStep";
import PlanDeveloppementManagerStep from "./steps/PlanDeveloppementManagerStep";
import ObjectifsNManagerStep from "./steps/ObjectifsNManagerStep";
import EntretienManagerStep from "./steps/EntretienManagerStep";

const firstOpenStep = (steps) => {
    const nMinusOne = steps.find((step) => step.code === "OBJECTIFS_N_MOINS_1");
    if (nMinusOne) return nMinusOne.code;
    const inProgress = steps.find((step) => step.status === "IN_PROGRESS");
    if (inProgress) return inProgress.code;
    const notStarted = steps.find((step) => step.status === "NOT_STARTED" && !step.blocked);
    if (notStarted) return notStarted.code;
    return steps.find((step) => !step.blocked)?.code || steps[0]?.code;
};

export default function PerformanceManagerEvaluationPage() {
    const { progressId } = useParams();
    const [searchParams] = useSearchParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const isValidProgressId = progressId && progressId !== "undefined" && progressId !== "null" && !isNaN(Number(progressId));
    const revisionMode = searchParams.get("mode") === "revision";
    const readOnly = searchParams.get("mode") === "consultation";

    const campaignCards = useSelector((state) => state.performance.campaignCards);
    const manager = useSelector((state) => state.performance.manager);
    const stepper = useSelector((state) => state.performance.progress.stepperByProgressId[String(progressId)]);
    const loading = useSelector((state) => state.performance.progress.stepperLoadingByProgressId[String(progressId)]);
    const error = useSelector((state) => state.performance.progress.stepperErrorByProgressId[String(progressId)]);
    const consolidation = useSelector((state) => state.performance.consolidation.byProgressId[String(progressId)]);
    const steps = useMemo(() => asArray(stepper?.steps), [stepper]);
    const [activeStep, setActiveStep] = useState(null);
    const campaigns = useMemo(() => sortCampaignsByDefault(campaignCards.data), [campaignCards.data]);
    const defaultCampaignId = campaigns[0]?.campagneId;
    const collaborateurs = asArray(defaultCampaignId ? manager.collaborateursByCampagneId[String(defaultCampaignId)] : []);
    const evaluationsATraiter = useMemo(
        () => collaborateurs.filter((collaborateur) => {
            return collaborateur.submittedAt && collaborateur.globalStatus !== "COMPLETED";
        }),
        [collaborateurs]
    );
    const managerLoading = campaignCards.loading || (defaultCampaignId ? manager.loadingByCampagneId[String(defaultCampaignId)] : false);
    const managerError = campaignCards.error || (defaultCampaignId ? manager.errorByCampagneId[String(defaultCampaignId)] : null);

    useEffect(() => {
        if (isValidProgressId) {
            dispatch(fetchPerformanceStepperRequest(progressId));
            dispatch(fetchProgressConsolidationRequest(progressId));
        }
    }, [dispatch, progressId, isValidProgressId]);

    useEffect(() => setActiveStep(null), [progressId]);

    useEffect(() => {
        if (!isValidProgressId) {
            dispatch(fetchPerformanceCampaignCardsRequest());
        }
    }, [dispatch, isValidProgressId]);

    useEffect(() => {
        if (!isValidProgressId && defaultCampaignId) {
            dispatch(fetchManagerPerformanceDashboardRequest(defaultCampaignId));
        }
    }, [defaultCampaignId, dispatch, isValidProgressId]);

    useEffect(() => {
        if (revisionMode) setActiveStep("OBJECTIFS_N_MOINS_1");
        else if (!activeStep && steps.length > 0) setActiveStep(stepper?.currentStep || firstOpenStep(steps));
    }, [activeStep, steps, revisionMode, stepper?.currentStep]);

    const handleSelectStep = (stepCode) => {
        setActiveStep(stepCode);
        dispatch(updatePerformanceCurrentStepRequest(progressId, stepCode));
    };

    if (!isValidProgressId) {
        return (
            <main className="perf-page">
                <header className="perf-header">
                    <div>
                        <h1>Evaluations a traiter</h1>
                        <p>{campaigns[0]?.nom || "Campagne performance"} - collaborateurs ayant commence leur evaluation</p>
                    </div>
                </header>
                {managerLoading && <div className="perf-state">Chargement des collaborateurs...</div>}
                {managerError && <div className="perf-state perf-state--error">{managerError}</div>}
                {!managerLoading && !managerError && !defaultCampaignId && <div className="perf-state">Aucune campagne performance disponible.</div>}
                {!managerLoading && !managerError && defaultCampaignId && evaluationsATraiter.length === 0 && (
                    <div className="perf-state">
                        <strong>Aucune evaluation a traiter</strong>
                        <p>Toutes les evaluations ont ete traitees ou aucune n'est en attente.</p>
                    </div>
                )}
                {!managerLoading && !managerError && evaluationsATraiter.length > 0 && (
                    <section className="perf-table-wrap">
                        <table className="perf-table">
                            <thead>
                            <tr>
                                <th>Collaborateur</th>
                                <th>Poste</th>
                                <th>Direction</th>
                                <th>Statut</th>
                                <th>Progression</th>
                                <th>Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            {evaluationsATraiter.map((collaborateur) => (
                                <tr key={collaborateur.evaluationProgressId}>
                                    <td>{collaborateur.collaborateurNom || "-"}</td>
                                    <td>{collaborateur.jobName || "-"}</td>
                                    <td>{collaborateur.entityName || "-"}</td>
                                    <td><span className={`perf-badge perf-badge--${statusTone(collaborateur.globalStatus)}`}>{formatStatus(collaborateur.globalStatus)}</span></td>
                                    <td>{collaborateur.progressionPourcentage ?? 0}%</td>
                                    <td>
                                        <button
                                            className="perf-btn perf-btn--primary"
                                            type="button"
                                            disabled={!collaborateur.evaluationProgressId}
                                            onClick={() => navigate(`/performance/manager/evaluation/${collaborateur.evaluationProgressId}`)}
                                        >
                                            Ouvrir evaluation
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </section>
                )}
            </main>
        );
    }

    const renderStep = () => {
        switch (activeStep) {
            case "OBJECTIFS_N_MOINS_1":
                return <ObjectifsNMoins1ManagerStep progressId={progressId} revisionMode={revisionMode} readOnly={readOnly} />;
            case "COMPETENCES_GENERIQUES":
                return <CompetencesGeneriquesManagerStep progressId={progressId} />;
            case "COMPETENCES_SPECIFIQUES":
                return <CompetencesSpecifiquesManagerStep progressId={progressId} />;
            case "PLAN_DEVELOPPEMENT":
                return <PlanDeveloppementManagerStep progressId={progressId} />;
            case "OBJECTIFS_N":
                return <ObjectifsNManagerStep progressId={progressId} />;
            case "ENTRETIEN_ANNUEL":
                return <EntretienManagerStep progressId={progressId} />;
            default:
                return <div className="perf-state">Sélectionnez une étape.</div>;
        }
    };

    return (
        <main className="perf-page">
            <header className="perf-header">
                <div>
                    <h1>{consolidation?.collaborateurNom || stepper?.collaborateurNom || "Evaluation collaborateur"}</h1>
                    <p>
                        {revisionMode ? "Révision des objectifs N-1" : (
                            <>
                                {stepper?.campagneNom || consolidation?.campagneNom || "Campagne performance"}
                                {stepper?.progressionPourcentage != null && (
                                    <> &nbsp;&middot;&nbsp;
                                        <span style={{ color: "#ef4444", fontWeight: 700 }}>
                                            {stepper.progressionPourcentage}% complet
                                        </span>
                                    </>
                                )}
                            </>
                        )}
                    </p>
                </div>
            </header>
            {loading && <div className="perf-state"><span style={{ display: "inline-block", width: 14, height: 14, border: "2px solid #e5e7eb", borderTopColor: "#ef4444", borderRadius: "50%", animation: "perf-spin .7s linear infinite", flexShrink: 0 }} />Chargement du stepper...</div>}
            {error && <div className="perf-state perf-state--error">⚠ {error}</div>}
            {!loading && !error && steps.length > 0 && (
                <>
                    {!revisionMode && <PerformanceStepper steps={steps} activeCode={activeStep} onSelect={handleSelectStep} />}
                    <section className="perf-step-panel">
                        <h2>{stepLabels[activeStep] || activeStep}</h2>
                        {renderStep()}
                        {!revisionMode && stepper?.globalStatus === "PENDING_VALIDATION" && (
                            <div className="perf-actions perf-actions--center">
                                <button className="perf-btn perf-btn--primary" type="button" onClick={() => dispatch(finalizeManagerPerformanceProgressRequest(progressId))}>
                                    Finaliser l'évaluation
                                </button>
                            </div>
                        )}
                        {!revisionMode && <PerformanceStepArrows steps={steps} activeCode={activeStep} onSelect={handleSelectStep} />}
                    </section>
                </>
            )}
        </main>
    );
}
